import {
    RiBuilding2Line, RiCloseCircleFill, RiCloseCircleLine,
    RiEdit2Line, RiListCheck,
} from "@remixicon/react";
import React, {
    ReactElement,
} from "react";
import {
    ChannelType,
    ChatSettings,
    ConversationSourceType,
    GetChannelListDocument,
    GetChannelTagsDocument,
    GetChatSettingsDocument,
    GetChatTagSettingsDocument,
    GetConversationSourcesDocument,
    GetSettingsDocument,
    GetSlackChannelSettingsDocument,
    useAddChannelTagToChannelsMutation,
    useCreateChannelTagMutation,
    useGetChatTagSettingsQuery,
    useRemoveChannelTagFromChannelsMutation,
    useSaveChatSettingsMutation,
} from "../../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../../api";
import useDeleteConfirm from "../../../../../components/confirm/Delete";
import { useFormBuilder } from "../../../../../components/dirty-tracker/FormTracker";
import { Filters as FiltersSelector } from "../../../../../components/filters/Filters";
import ConversationSources from "../../../../../components/pickers/conversation-sources/ConversationSources";
import Badge from "../../../../../components/theme/badge/Badge";
import { useDrawerForm } from "../../../../../components/theme/drawer";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../../components/theme/settings";
import createTableContainer from "../../../../../components/theme/table";
import Text from "../../../../../components/theme/text/Text";
import Toggle from "../../../../../components/theme/toggle/Toggle";
import useArticleContext from "../../../../../contexts/HelpContext";
import useRouting from "../../../../../contexts/Routing";
import { DEEP_COMPARER, SettingsProps, SOURCES } from "../../base";
import { DisplayChatSettings } from "../../chat/ChatV3";
import { ChatSettingsWithName, useChannelSettingsList } from "../channel-list/ChannelList";

export function isEmpty(obj: unknown, name: string | undefined | null): boolean {
    if (obj == null || obj === "") {
        return true;
    }
    if (Array.isArray(obj)) {
        return obj.every(row => isEmpty(row, name));
    }

    if (typeof obj === "object") {
        return Object.entries(obj).every(([key, value]) => key === "__typename"
            || (key === "name" && value.includes(name))
            || isEmpty(value, name));
    }

    return false;
}

const TableContainer = createTableContainer<ChatSettingsWithName>();

export default function ChannelTag({
    id, settings,
}: {
    id: string;
} & SettingsProps): ReactElement {
    useArticleContext("urn:fq:article:22c4892ab6ad48d5ac58d5a9f72559c4");

    const { historyPush, Paths } = useRouting();

    const isNew = id === "new";
    const { data, error, loading: isLoading } = useGetChatTagSettingsQuery({
        skip: isNew,
        variables: {
            id,
            tag: id,
        },
    });
    const [save] = useSaveChatSettingsMutation();

    const {
        shouldShowWorkspaces,
        workspacesList,
        channelList,
    } = useChannelSettingsList(data?.tagChannels as ChatSettings[]);

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .proxy("chatSettings", () => (data?.chatSettings ?? {}) as ChatSettings, DEEP_COMPARER)
            .property("name", () => data?.chatSettings?.options?.tag?.name ?? "")
            .property("isCustom", () => !isEmpty(data?.chatSettings, data?.chatSettings?.options?.tag?.name)),
        [data?.chatSettings],
    );

    const [saveTag] = useCreateChannelTagMutation();
    const [addTagToChannels] = useAddChannelTagToChannelsMutation();
    const [removeTagFromChannels] = useRemoveChannelTagFromChannelsMutation();

    const { element } = tracker.useDirtyTracker({
        finishedSavingMessage: "Channel Group saved.",
        onSave: async (__, onDone) => {
            let chatId = id;
            if (isNew) {
                const created = await saveTag({
                    refetchQueries: [
                        { query: GetChannelTagsDocument },
                        { query: GetConversationSourcesDocument },
                        { query: GetSettingsDocument },
                    ],
                    variables: {
                        channelTag: {
                            name: properties.name.value,
                        },
                    },
                });
                chatId = created.data?.createChannelTag?.chatSettingsId;
            }

            await save({
                refetchQueries: [
                    { query: GetSlackChannelSettingsDocument },
                    { query: GetChatSettingsDocument, variables: { id } },
                ],
                variables: {
                    id: chatId,
                    settings: cloneWithoutTypename(properties.chatSettings.serialize() as ChatSettings),
                },
            });
            onDone();
            if (id === "new") {
                historyPush(Paths.ADMINISTRATOR_SETTING_ID, {
                    setting: "channel-groups",
                    id: chatId,
                });
            }
        },
    });

    const { properties: selectedIdProperties } = useFormBuilder(
        builder => builder.property("selectedIds", () => new Set<string>()),
        [],
    );

    const { element: drawerElement, setOpen, formTracker } = useDrawerForm({
        title: "Add Channels to Group",
        onHide: () => {
            formTracker.reset();
        },
        onSave: async () => {
            await addTagToChannels({
                refetchQueries: [
                    { query: GetChannelListDocument },
                    { query: GetChatTagSettingsDocument, variables: { id, tag: id } },
                ],
                variables: {
                    channelTag: {
                        tag: id,
                        channelIds: formTracker.properties.channelIds.value,
                    },

                },
            });
            formTracker.reset();
        },
        formTrackerBuilder: {
            handler: builder => builder
                .array("channelIds", () => [] as string[]),
            dependencies: [],
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        body: createTracker => (
            <FormGroup label="Channels" size="medium">
                <ConversationSources
                    sourceTypes={[ConversationSourceType.MicrosoftTeams, ConversationSourceType.SlackChannel]}
                    isMulti
                    shouldIncludeTags={false}
                    value={createTracker.properties.channelIds.value}
                    onSelected={createTracker.properties.channelIds.setValue}
                />
            </FormGroup>
        ),
    });

    const removeTagFromChannelList = async (channelIds: string[]) => {
        await removeTagFromChannels({
            refetchQueries: [
                { query: GetChannelListDocument },
                { query: GetChatTagSettingsDocument, variables: { id, tag: id } },
            ],
            variables: {
                channelTag: {
                    tag: id,
                    channelIds,
                },
            },
        });
    };

    const { element: deleteElement, setDeleting } = useDeleteConfirm<string[]>(
        async channelIds => {
            removeTagFromChannelList(channelIds);
            selectedIdProperties.selectedIds.setValue(new Set());
        },
        () => `${properties.name.value}`,
        "Remove Channels from group",
        `Do you want to remove ${selectedIdProperties.selectedIds.value.size} items from group`,
        "Remove",
    );

    return (
        <SettingsContainer saveElement={element}>
            <SettingsContainer.Section title="Group Info">
                <SettingsContainer.Setting
                    title="Group Name"
                    description="A label used to organize and manage multiple channels under a common setting or category."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <FormGroup
                        onChange={properties.name.setValueFromInput}
                        value={properties.name.value}
                        size="small"
                        isRequired
                        isDisabled={!isNew}
                    />
                </SettingsContainer.Setting>
                <SettingsContainer.Setting
                    title="Custom Settings"
                    description="Configure settings for your group to meet specific needs
                        and preferences. If turned OFF, Global Settings will
                        apply to this group."
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isLoading={isLoading}
                >
                    <Toggle
                        onChange={properties.isCustom.setCheckedFromInput}
                        checked={properties.isCustom.value}
                    />

                </SettingsContainer.Setting>
            </SettingsContainer.Section>

            {properties.isCustom.value && (
                <DisplayChatSettings
                    chatSettings={properties.chatSettings}
                    settings={settings}
                    shouldAllowSlack
                    isLoading={isLoading}
                />
            )}

            <SettingsContainer.Section
                title="Channels"
                subtitle={!isLoading && channelList.isNotEmpty
                    ? <Badge variant="counter" isSelected>{channelList.length}</Badge>
                    : undefined}
                helpText="The list of channels which have the current group applied. You can add
                    channels or remove channels from this group using the list below."
            >
                <SettingsContainer.Setting
                    title="Channels"
                    description="The list of channels which are a member of this channel group"
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                    isHideTitle
                >
                    <TableContainer data={channelList} isLoading={isLoading} error={error}>
                        <TableContainer.Empty>
                            <TableContainer.Empty.Default
                                text="Here you can manage the list of channels which are a member of this channel group.
                                    You can view channels, add, or remove channels from the group."
                                title="Member Channels"
                                icon={<RiListCheck />}
                                buttonText="Add Channels to Group"
                                onClick={() => setOpen(true)}
                            />
                        </TableContainer.Empty>

                        <TableContainer.TableControls>

                            <TableContainer.TableControls.Search
                                search={(query, item) => item.name?.toLocaleLowerCase().includes(query)
                                    || item.tagList?.toLocaleLowerCase().includes(query)
                                    || (shouldShowWorkspaces && item.workspaceName?.toLocaleLowerCase().includes(query))
                                    || item.type?.toLocaleLowerCase().includes(query)}
                            />
                            {selectedIdProperties.selectedIds.value.size > 0 ? (
                                <>
                                    <TableContainer.TableControls.Button
                                        variant="outline-blue"
                                        postfixIcon={<RiCloseCircleFill />}
                                        onClick={() => selectedIdProperties.selectedIds.setValue(new Set())}
                                        size="small"
                                        // className={Styles["button-clear"]}
                                        text={`${selectedIdProperties.selectedIds.value.size} selected`}
                                    />
                                    <TableContainer.TableControls.Seperator />
                                    <TableContainer.TableControls.Button
                                        variant="danger"
                                        onClick={() => setDeleting(Array.from(selectedIdProperties.selectedIds.value))}
                                        size="small"
                                        text="Remove Channels from Group"
                                    />
                                </>

                            ) : (
                                <>

                                    <TableContainer.TableControls.Seperator />
                                    <TableContainer.TableControls.Filters />

                                    <TableContainer.TableControls.Filter
                                        defaultValue={new Set<string>()}
                                        deserialize={TableContainer.TableControls.Filter.Serializers.deserializeSet}
                                        serialize={TableContainer.TableControls.Filter.Serializers.serializeSet}
                                        filterKey="workspace"
                                        onFilter={(item, selected) => selected.has(item.workspaceName ?? "")}
                                        isEqual={(left, right) => Array.from(left ?? []).equals(Array.from(right ?? []))}
                                    >
                                        {(currentValue, setValue) => (
                                            <FiltersSelector
                                                toggleText="Workspace"
                                                categories={workspacesList}
                                                icon={<RiBuilding2Line />}
                                                searchMinimum={5}
                                                selectedCategories={currentValue}
                                                onSelected={setValue}
                                            />
                                        )}

                                    </TableContainer.TableControls.Filter>

                                    <TableContainer.TableControls.SortBy />
                                    <TableContainer.TableControls.Create
                                        text="Add Channels to Group"
                                        onClick={() => setOpen(true)}
                                    />
                                </>
                            )}
                        </TableContainer.TableControls>
                        <TableContainer.Table>
                            <TableContainer.RowSelector
                                selection={selectedIdProperties.selectedIds.value}
                                onSelectionChange={selection => {
                                    selectedIdProperties.selectedIds.setValue(selection);
                                }}
                            />

                            <TableContainer.TableValue
                                columnKey="name"
                                title="Name"
                                sortBy={item => item.name?.toLowerCase()}
                            >
                                {item => item.name}
                            </TableContainer.TableValue>
                            <TableContainer.TableValue
                                columnKey="source"
                                title="Source"
                                sortBy={item => item.type?.toLowerCase()}
                            >
                                {item => {
                                    const source = SOURCES[item.type as ChannelType];
                                    return (
                                        <Text
                                            textVariant="body-2-regular"
                                        >
                                            {source.tableIcon}
                                            {" "}
                                            {source.text}
                                        </Text>
                                    );
                                }}
                            </TableContainer.TableValue>
                            {shouldShowWorkspaces && (
                                <TableContainer.TableValue
                                    columnKey="workspace"
                                    title="Workspace"
                                    sortBy={item => item.workspaceName?.toLowerCase()}
                                >
                                    {item => <Badge variant="primary">{item.workspaceName}</Badge>}
                                </TableContainer.TableValue>
                            )}

                            <TableContainer.Action
                                icon={<RiEdit2Line />}
                                altText="Edit Channel Settings"
                                to={Paths.ADMINISTRATOR_SETTING_ID}
                                getLinkParams={item => ({ setting: "chat", id: item.id })}
                            />
                            <TableContainer.Action
                                icon={<RiCloseCircleLine />}
                                altText="Remove channel from group"
                                onClick={item => removeTagFromChannelList([item.id])}
                            />

                        </TableContainer.Table>
                    </TableContainer>
                </SettingsContainer.Setting>
            </SettingsContainer.Section>
            {drawerElement}
            {deleteElement}
        </SettingsContainer>
    );
}
